/**
 * Parse/serialise for `patient_app_key_nutrients`, stored as a CSV string.
 *
 * Extracted so the round trip is testable on its own. The bug this exists to
 * prevent (FOOD-1315) was a seeding mistake, not a rendering one: the chips
 * started from an empty set when the value came from the clinic default, so
 * clicking one chip replaced the whole selection instead of adding to it.
 */

/** Maximum nutrients the RxFood app's summary card can display. */
export const MAX_KEY_NUTRIENTS = 4;

/** CSV → ordered, de-duplicated list. Tolerates stray spaces and empties. */
export const parseKeyNutrients = (csv: unknown): string[] => {
  if (typeof csv !== 'string') return [];
  const out: string[] = [];
  for (const part of csv.split(',')) {
    const v = part.trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
};

/** List → the CSV shape the server stores (", " separated, matching v1). */
export const serializeKeyNutrients = (nutrients: string[]): string => nutrients.join(', ');

/**
 * The selection to show: the patient's own override when they have one,
 * otherwise whatever the clinic resolves to.
 *
 * `override === undefined` means "no per-patient value" and must fall through
 * to the clinic. An override of `''` is a real choice — the clinician
 * explicitly picked none — and must NOT fall through, or they could never
 * turn the card's nutrients off.
 */
export const resolveKeyNutrients = (override: unknown, clinicDefault: unknown): string[] =>
  override === undefined ? parseKeyNutrients(clinicDefault) : parseKeyNutrients(override);

/**
 * Toggle one nutrient within `selected`, capped at `MAX_KEY_NUTRIENTS`.
 * Returns the list unchanged when adding would exceed the cap, so the caller
 * can tell nothing happened.
 */
export const toggleKeyNutrient = (selected: string[], nutrient: string): string[] => {
  if (selected.includes(nutrient)) return selected.filter((n) => n !== nutrient);
  if (selected.length >= MAX_KEY_NUTRIENTS) return selected;
  return [...selected, nutrient];
};
